import {
  ButtonGroup,
  HStack,
  IconButton,
  NativeSelect,
  Pagination,
  Text,
} from '@chakra-ui/react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import type React from 'react'
import { Trans, useTranslation } from 'react-i18next'

interface TableFooterProps {
  page: number
  totalPages: number
  hasNextPage: boolean
  onFirstPage: () => void
  onPreviousPage: () => void
  onNextPage: () => void
  onLastPage: () => void
  pageSize?: number
  pageSizeOptions?: number[]
  onPageSizeChange?: (pageSize: number) => void
  /**
   * Jump straight to a page. When given, the footer renders numbered page
   * buttons; without it there is no way to honour an arbitrary jump, so it
   * falls back to a "Page x of y" label and step-by-step navigation.
   */
  onPageChange?: (page: number) => void
  prepend?: React.ReactNode
}

const TableFooter: React.FC<TableFooterProps> = ({
  page,
  totalPages,
  hasNextPage,
  onFirstPage,
  onPreviousPage,
  onNextPage,
  onLastPage,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  onPageChange,
  prepend,
}) => {
  const { t } = useTranslation(undefined, { useSuspense: false })

  // `Pagination.Root` counts items, not pages. Callers only know their page
  // count, so hand it a synthetic total that divides back to the same number
  // of pages.
  const effectivePageSize = pageSize ?? 1
  const count = totalPages * effectivePageSize

  function handlePageChange(details: Pagination.PageChangeDetails) {
    if (onPageChange) {
      onPageChange(details.page)
      return
    }
    // Without a jump handler the triggers can still only move one step, so
    // map them onto the step callbacks the caller did provide.
    if (details.page === page - 1) {
      onPreviousPage()
    } else if (details.page === page + 1) {
      onNextPage()
    }
  }

  return (
    <HStack
      width="100%"
      justifyContent={prepend ? 'space-between' : 'flex-end'}
      gap={6}
      mt={4}
    >
      {prepend}
      {pageSize != null && pageSizeOptions && onPageSizeChange && (
        <HStack gap={2} data-testid="page-size-selector">
          <NativeSelect.Root size="sm" width="auto" bg="bg">
            <NativeSelect.Field
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.currentTarget.value))}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <Text fontSize="sm" whiteSpace="nowrap" color="fg.muted">
            {t('pagination.rowsPerPage')}
          </Text>
        </HStack>
      )}

      <Pagination.Root
        count={count}
        pageSize={effectivePageSize}
        page={page}
        onPageChange={handlePageChange}
      >
        {/* Chakra's documented Pagination composition: a ghost `sm`
            ButtonGroup with chevron triggers around numbered items that
            switch to the outline variant when selected, collapsing the
            middle to an ellipsis once there are too many pages.

            First/last buttons only appear on the fallback path. With numbers
            you can already jump to the last page by clicking it; without
            them, callers would lose that reach entirely. */}
        <ButtonGroup variant="ghost" size="sm">
          {!onPageChange && (
            <IconButton
              onClick={onFirstPage}
              disabled={page === 1}
              aria-label={String(t('pagination.firstPage'))}
              data-testid="first-page"
              bg="bg"
            >
              <ChevronsLeft />
            </IconButton>
          )}

          <Pagination.PrevTrigger asChild>
            <IconButton
              aria-label={String(t('pagination.previousPage'))}
              data-testid="previous-page"
              bg="bg"
            >
              <ChevronLeft />
            </IconButton>
          </Pagination.PrevTrigger>

          {onPageChange ? (
            <Pagination.Items
              render={(pageItem) => (
                <IconButton
                  variant={{ base: 'ghost', _selected: 'outline' }}
                  aria-label={String(
                    t('pagination.goToPage', { page: pageItem.value }),
                  )}
                  bg="bg"
                >
                  {pageItem.value}
                </IconButton>
              )}
            />
          ) : (
            <Text
              alignSelf="center"
              px={2}
              fontSize="sm"
              data-testid="current-page"
            >
              <Trans i18nKey="pagination.page" values={{ page, totalPages }} />
            </Text>
          )}

          <Pagination.NextTrigger asChild>
            {/* `hasNextPage` is the caller's word on whether more data
                exists, which a server-paginated table knows better than the
                synthetic count above. */}
            <IconButton
              disabled={!hasNextPage}
              aria-label={String(t('pagination.nextPage'))}
              data-testid="next-page"
              bg="bg"
            >
              <ChevronRight />
            </IconButton>
          </Pagination.NextTrigger>

          {!onPageChange && (
            <IconButton
              onClick={onLastPage}
              disabled={page >= totalPages}
              aria-label={String(t('pagination.lastPage'))}
              data-testid="last-page"
              bg="bg"
            >
              <ChevronsRight />
            </IconButton>
          )}
        </ButtonGroup>
      </Pagination.Root>
    </HStack>
  )
}

export default TableFooter
